import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Divider,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { authService, companyAdminService, type AdminCompany, type UpdateAdminCompanyPayload } from '../services';
import { COMPANY_PLAN_OPTIONS, derivePlanCapabilities } from '../constants/companyPlans';
import { PixKeyType, type CompanyPlan } from '../services/companyConfigService';

interface EditDialogState {
  open: boolean;
  company: AdminCompany | null;
}

const formatCnpj = (value?: string): string => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 14);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  }
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
};

const formatWhatsapp = (value?: string): string => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 11);
  if (!digits) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const formatCep = (value?: string): string => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

const sanitizeDigits = (value: string, maxLength?: number): string => {
  const digits = value.replace(/\D/g, '');
  if (typeof maxLength === 'number') {
    return digits.slice(0, maxLength);
  }
  return digits;
};

const WORKING_DAYS_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: 'Domingo' },
  { value: 1, label: 'Segunda-feira' },
  { value: 2, label: 'Terça-feira' },
  { value: 3, label: 'Quarta-feira' },
  { value: 4, label: 'Quinta-feira' },
  { value: 5, label: 'Sexta-feira' },
  { value: 6, label: 'Sábado' },
];

const toDateInputValue = (value?: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const PIX_KEY_TYPES: PixKeyType[] = ['CPF', 'CNPJ', 'EMAIL', 'PHONE', 'EVP'];

interface EditFormState {
  razaoSocial: string;
  slug: string;
  customerPortalUrl: string;
  whatsappPhone: string;
  message: string;
  plan: CompanyPlan;
  endereco: {
    rua: string;
    numero: string;
    bairro: string;
    cidade: string;
    estado: string;
    cep: string;
    complemento: string;
  };
  diasFuncionamento: number[];
  dataInicioUsoPlataforma: string; // formato yyyy-mm-dd
  modules: {
    paymentsEnabled: boolean;
    paymentsPixKey: string;
    paymentsPixKeyType: PixKeyType | '';
    inventoryEnabled: boolean;
  };
}

const EMPTY_EDIT_FORM: EditFormState = {
  razaoSocial: '',
  slug: '',
  customerPortalUrl: '',
  whatsappPhone: '',
  message: '',
  plan: 'VITRINE_APP',
  endereco: {
    rua: '',
    numero: '',
    bairro: '',
    cidade: '',
    estado: '',
    cep: '',
    complemento: '',
  },
  diasFuncionamento: [1, 2, 3, 4, 5],
  dataInicioUsoPlataforma: '',
  modules: {
    paymentsEnabled: false,
    paymentsPixKey: '',
    paymentsPixKeyType: '',
    inventoryEnabled: false,
  },
};

const CompanyAdminList: React.FC = () => {
  const userRole = authService.getUserRole();
  const [companies, setCompanies] = useState<AdminCompany[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editDialog, setEditDialog] = useState<EditDialogState>({ open: false, company: null });
  const [editForm, setEditForm] = useState<EditFormState>(EMPTY_EDIT_FORM);
  const [saving, setSaving] = useState<boolean>(false);

  const canAccess = useMemo(() => userRole === 'DEVELOPER', [userRole]);
  const planCapabilities = useMemo(() => derivePlanCapabilities(editForm.plan), [editForm.plan]);
  const selectedPlanOption = useMemo(
    () => COMPANY_PLAN_OPTIONS.find((option) => option.value === editForm.plan) ?? COMPANY_PLAN_OPTIONS[0],
    [editForm.plan],
  );

  const loadCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      const rows = await companyAdminService.listCompanies();
      setCompanies(rows);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canAccess) {
      loadCompanies();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccess]);

  const handleCloseDialog = () => {
    setEditDialog({ open: false, company: null });
    setEditForm(EMPTY_EDIT_FORM);
  };

  const handleOpenDialog = (company: AdminCompany) => {
    const plan = company.plan ?? 'VITRINE_APP';
    const capabilities = derivePlanCapabilities(plan);
    const modules = company.modules ?? {};

    setEditForm({
      razaoSocial: company.razaoSocial ?? '',
      slug: company.slug ?? '',
      customerPortalUrl: company.customerPortalUrl ?? '',
      whatsappPhone: company.whatsappPhone ?? '',
      message: company.message ?? '',
      plan,
      endereco: {
        rua: company.endereco?.rua ?? '',
        numero: company.endereco?.numero ?? '',
        bairro: company.endereco?.bairro ?? '',
        cidade: company.endereco?.cidade ?? '',
        estado: company.endereco?.estado ?? '',
        cep: company.endereco?.cep ?? '',
        complemento: company.endereco?.complemento ?? '',
      },
      diasFuncionamento: Array.isArray(company.diasFuncionamento) ? company.diasFuncionamento : [],
      dataInicioUsoPlataforma: toDateInputValue(company.dataInicioUsoPlataforma),
      modules: {
        paymentsEnabled: modules.payments?.enabled ?? capabilities.paymentsEnabled,
        paymentsPixKey: modules.payments?.pixKey ?? '',
        paymentsPixKeyType: (modules.payments?.pixKeyType as PixKeyType | undefined) ?? '',
        inventoryEnabled: modules.inventory?.enabled ?? capabilities.inventoryEnabled,
      },
    });
    setEditDialog({ open: true, company });
  };

  const handleUpdateCompany = async (companyId: string, payload: UpdateAdminCompanyPayload, successMsg?: string) => {
    try {
      setSaving(true);
      setError(null);
      const updated = await companyAdminService.updateCompany(companyId, payload);
      setCompanies((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      if (successMsg) {
        setSuccess(successMsg);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao atualizar empresa');
    } finally {
      setSaving(false);
    }
  };

  const handlePlanChange = (plan: CompanyPlan) => {
    const capabilities = derivePlanCapabilities(plan);
    setEditForm((prev) => ({
      ...prev,
      plan,
      modules: {
        ...prev.modules,
        paymentsEnabled: capabilities.paymentsEnabled ? prev.modules.paymentsEnabled : false,
        paymentsPixKey: capabilities.paymentsEnabled ? prev.modules.paymentsPixKey : '',
        paymentsPixKeyType: capabilities.paymentsEnabled ? prev.modules.paymentsPixKeyType : '',
        inventoryEnabled: capabilities.inventoryEnabled ? prev.modules.inventoryEnabled : false,
      },
    }));
  };

  const handleBasicFieldChange = <
    K extends keyof Pick<
      EditFormState,
      'razaoSocial' | 'slug' | 'customerPortalUrl' | 'whatsappPhone' | 'message' | 'dataInicioUsoPlataforma'
    >
  >(
    field: K,
    value: EditFormState[K],
  ) => {
    setEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddressChange = (field: keyof EditFormState['endereco'], value: string) => {
    setEditForm((prev) => ({
      ...prev,
      endereco: {
        ...prev.endereco,
        [field]: value,
      },
    }));
  };

  const handleModuleChange = (field: keyof EditFormState['modules'], value: boolean | string) => {
    setEditForm((prev) => ({
      ...prev,
      modules: {
        ...prev.modules,
        [field]: value,
      },
    }));
  };

  const handleToggleWorkingDay = (day: number) => {
    setEditForm((prev) => {
      const exists = prev.diasFuncionamento.includes(day);
      const diasFuncionamento = exists
        ? prev.diasFuncionamento.filter((value) => value !== day)
        : [...prev.diasFuncionamento, day];
      return {
        ...prev,
        diasFuncionamento,
      };
    });
  };

  const handleSubmitEdit = async () => {
    if (!editDialog.company) return;

    const payload: UpdateAdminCompanyPayload = {
      razaoSocial: editForm.razaoSocial.trim() || undefined,
      slug: editForm.slug.trim() || undefined,
      customerPortalUrl: editForm.customerPortalUrl.trim() || undefined,
      whatsappPhone: editForm.whatsappPhone.trim() || undefined,
      message: editForm.message.trim() || undefined,
      plan: editForm.plan,
      endereco: {
        rua: editForm.endereco.rua.trim(),
        numero: editForm.endereco.numero.trim(),
        bairro: editForm.endereco.bairro.trim(),
        cidade: editForm.endereco.cidade.trim(),
        estado: editForm.endereco.estado.trim(),
        cep: sanitizeDigits(editForm.endereco.cep, 8),
        complemento: editForm.endereco.complemento.trim() || undefined,
      },
      diasFuncionamento: editForm.diasFuncionamento.sort((a, b) => a - b),
      dataInicioUsoPlataforma: editForm.dataInicioUsoPlataforma || null,
      modules: editForm.modules.paymentsEnabled || editForm.modules.inventoryEnabled
        ? {
            payments: {
              enabled: editForm.modules.paymentsEnabled,
              pixKey: editForm.modules.paymentsPixKey.trim() || undefined,
              pixKeyType: editForm.modules.paymentsPixKeyType || undefined,
            },
            inventory: {
              enabled: editForm.modules.inventoryEnabled,
            },
          }
        : {
            payments: { enabled: false },
            inventory: { enabled: false },
          },
    };

    await handleUpdateCompany(editDialog.company.id, payload, 'Dados da empresa atualizados.');
    handleCloseDialog();
  };

  const handleToggleBillingBlock = async (company: AdminCompany) => {
    await handleUpdateCompany(
      company.id,
      { billingBlocked: !company.billingBlocked },
      !company.billingBlocked ? 'Empresa bloqueada por inadimplência.' : 'Bloqueio removido com sucesso.',
    );
  };

  const handleToggleActive = async (company: AdminCompany) => {
    await handleUpdateCompany(
      company.id,
      { active: !company.active },
      !company.active ? 'Empresa reativada.' : 'Empresa inativada.',
    );
  };

  if (!canAccess) {
    return (
      <Box maxWidth="md" mx="auto" mt={4}>
        <Alert severity="error">Apenas usuários DEVELOPER podem acessar a gestão de empresas.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2} mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Gestão de empresas
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Visualize empresas cadastradas, atualize dados básicos e controle bloqueios por inadimplência.
          </Typography>
        </Box>
        <Tooltip title="Atualizar lista">
          <span>
            <Button startIcon={<RefreshIcon />} variant="outlined" onClick={loadCompanies} disabled={loading}>
              Recarregar
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table size="medium">
            <TableHead>
              <TableRow>
                <TableCell>Empresa</TableCell>
                <TableCell>CNPJ</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell>WhatsApp</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Bloqueio</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Box display="flex" alignItems="center" justifyContent="center" py={4}>
                      <CircularProgress size={32} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : companies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body1" color="text.secondary" py={4}>
                      Nenhuma empresa cadastrada até o momento.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                companies.map((company) => (
                  <TableRow key={company.id} hover>
                    <TableCell>
                      <Stack direction="column" spacing={0.5}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {company.razaoSocial || '---'}
                        </Typography>
                        {company.message && (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {company.message}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{formatCnpj(company.cnpj)}</TableCell>
                    <TableCell>{company.slug || '—'}</TableCell>
                    <TableCell>{formatWhatsapp(company.whatsappPhone)}</TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={company.active ? 'Ativa' : 'Inativa'}
                        color={company.active ? 'success' : 'default'}
                        icon={company.active ? <CheckCircleIcon fontSize="small" /> : undefined}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        <Switch
                          checked={company.billingBlocked}
                          onChange={() => handleToggleBillingBlock(company)}
                          color="error"
                          inputProps={{ 'aria-label': 'Bloquear por inadimplência' }}
                        />
                        <Typography variant="body2" color={company.billingBlocked ? 'error.main' : 'text.secondary'}>
                          {company.billingBlocked ? 'Bloqueada' : 'Liberada'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Tooltip title={company.active ? 'Inativar empresa' : 'Reativar empresa'}>
                          <span>
                            <IconButton
                              onClick={() => handleToggleActive(company)}
                              color={company.active ? 'default' : 'success'}
                              disabled={saving}
                              size="small"
                            >
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Editar dados">
                          <span>
                            <IconButton onClick={() => handleOpenDialog(company)} color="primary" disabled={saving} size="small">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={editDialog.open} onClose={handleCloseDialog} fullWidth maxWidth="md">
        <DialogTitle>Editar empresa</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} mt={1}>
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Dados principais
              </Typography>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Razão social"
                    value={editForm.razaoSocial}
                    onChange={(event) => handleBasicFieldChange('razaoSocial', event.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Slug público"
                    value={editForm.slug}
                    onChange={(event) => handleBasicFieldChange('slug', event.target.value)}
                    helperText="Utilizado na URL pública do delivery"
                    fullWidth
                  />
                </Stack>
                <TextField
                  label="URL do portal do cliente"
                  value={editForm.customerPortalUrl}
                  onChange={(event) => handleBasicFieldChange('customerPortalUrl', event.target.value.trim())}
                  placeholder="https://minhaloja.com.br"
                  fullWidth
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="CNPJ"
                    value={formatCnpj(editDialog.company?.cnpj)}
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    label="WhatsApp"
                    value={formatWhatsapp(editForm.whatsappPhone)}
                    onChange={(event) => {
                      const digits = event.target.value.replace(/\D/g, '').slice(0, 11);
                      handleBasicFieldChange('whatsappPhone', digits);
                    }}
                    placeholder="(48) 99999-9999"
                    fullWidth
                  />
                </Stack>
                <TextField
                  label="Mensagem para o cardápio"
                  value={editForm.message}
                  onChange={(event) => handleBasicFieldChange('message', event.target.value)}
                  multiline
                  minRows={3}
                  fullWidth
                />
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Funcionamento
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Início de uso da plataforma"
                  type="date"
                  value={editForm.dataInicioUsoPlataforma}
                  onChange={(event) => handleBasicFieldChange('dataInicioUsoPlataforma', event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Stack>
              <FormGroup row sx={{ mt: 1 }}>
                {WORKING_DAYS_OPTIONS.map((day) => (
                  <FormControlLabel
                    key={day.value}
                    control={
                      <Checkbox
                        checked={editForm.diasFuncionamento.includes(day.value)}
                        onChange={() => handleToggleWorkingDay(day.value)}
                      />
                    }
                    label={day.label}
                  />
                ))}
              </FormGroup>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Endereço
              </Typography>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Rua"
                    value={editForm.endereco.rua}
                    onChange={(event) => handleAddressChange('rua', event.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Número"
                    value={editForm.endereco.numero}
                    onChange={(event) => handleAddressChange('numero', event.target.value.slice(0, 10))}
                    fullWidth
                  />
                  <TextField
                    label="Complemento"
                    value={editForm.endereco.complemento}
                    onChange={(event) => handleAddressChange('complemento', event.target.value)}
                    fullWidth
                  />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Bairro"
                    value={editForm.endereco.bairro}
                    onChange={(event) => handleAddressChange('bairro', event.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Cidade"
                    value={editForm.endereco.cidade}
                    onChange={(event) => handleAddressChange('cidade', event.target.value)}
                    fullWidth
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Estado"
                    value={editForm.endereco.estado}
                    onChange={(event) => handleAddressChange('estado', event.target.value.slice(0, 2).toUpperCase())}
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                    fullWidth
                  />
                  <TextField
                    label="CEP"
                    value={formatCep(editForm.endereco.cep)}
                    onChange={(event) => {
                      const digits = sanitizeDigits(event.target.value, 8);
                      handleAddressChange('cep', digits);
                    }}
                    placeholder="00000-000"
                    fullWidth
                  />
                </Stack>
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Plano e módulos
              </Typography>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    select
                    label="Plano contratato"
                    value={editForm.plan}
                    onChange={(event) => handlePlanChange(event.target.value as CompanyPlan)}
                    fullWidth
                  >
                    {COMPANY_PLAN_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Status do plano"
                    value={selectedPlanOption.focus}
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                </Stack>
                <Box
                  sx={{
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    p: 2,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    {selectedPlanOption.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {selectedPlanOption.description}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Setup: {selectedPlanOption.setupFee} · {selectedPlanOption.monthlyPrice}
                  </Typography>
                </Box>
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editForm.modules.paymentsEnabled}
                        onChange={(event) => {
                          const enabled = event.target.checked && planCapabilities.paymentsEnabled;
                          handleModuleChange('paymentsEnabled', enabled);
                          if (!enabled) {
                            handleModuleChange('paymentsPixKey', '');
                            handleModuleChange('paymentsPixKeyType', '');
                          }
                        }}
                        disabled={!planCapabilities.paymentsEnabled}
                      />
                    }
                    label="Módulo de pagamentos (PIX)"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editForm.modules.inventoryEnabled}
                        onChange={(event) => {
                          const enabled = event.target.checked && planCapabilities.inventoryEnabled;
                          handleModuleChange('inventoryEnabled', enabled);
                        }}
                        disabled={!planCapabilities.inventoryEnabled}
                      />
                    }
                    label="Módulo de estoque"
                  />
                </FormGroup>
                {editForm.modules.paymentsEnabled && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      select
                      label="Tipo de chave PIX"
                      value={editForm.modules.paymentsPixKeyType}
                      onChange={(event) => handleModuleChange('paymentsPixKeyType', event.target.value as PixKeyType)}
                      fullWidth
                    >
                      {PIX_KEY_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label="Chave PIX"
                      value={editForm.modules.paymentsPixKey}
                      onChange={(event) => handleModuleChange('paymentsPixKey', event.target.value.slice(0, 60))}
                      helperText="Informe a chave cadastrada no banco"
                      fullWidth
                    />
                  </Stack>
                )}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSubmitEdit} disabled={saving} variant="contained" color="primary">
            {saving ? 'Salvando...' : 'Salvar alterações'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyAdminList;
